/**
 * Bundle module - quantity tiers ("quasi bundles").
 *
 * Renders pack-tier pricing on a product page and forces the chosen quantity
 * onto the product form at submit time. The line stays a single SKU; the real
 * price reduction comes from a Shopify automatic discount with a minimum
 * quantity requirement.
 *
 * All money strings are precomputed in Liquid (see
 * snippets/bundle-quantity-tiers.liquid). This file never formats currency.
 */
if (!customElements.get('bundle-quantity-tiers')) {
  class BundleQuantityTiers extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('.bundle-tiers__data');
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      this.section = document.getElementById(`shopify-section-${this.dataset.sectionId}`) || this;
      this.savingsEl = this.querySelector('.bundle-tiers__savings');
      this.packSizeEl = this.querySelector('.bundle-tiers__pack-size');
      this.inputs = Array.from(this.querySelectorAll('.bundle-tiers__input'));

      // The theme's own price nodes, rendered by snippets/price.liquid with
      // for_variant_picker: true (so .price__was is always present).
      this.themePrice = this.section.querySelector('.product-price .price');
      this.themeCurrent = this.themePrice && this.themePrice.querySelector('.price__current');
      this.themeWas = this.themePrice && this.themePrice.querySelector('.price__was');

      this.variantId = this.currentVariantId();
      this.selected = 'single';
      this.hasOverridden = false;

      this.moveSavingsUnderPrice();

      this.addEventListener('change', this.onTierChange.bind(this));

      // assets/variant-picker.js rewrites .price__current on every variant
      // change, and dispatches on:variant:change afterwards. Re-sync from that
      // event so a variant switch cannot leave stale pack pricing on screen.
      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

      // Capture phase so the quantity is set before the theme serialises the form.
      this.section.addEventListener('submit', this.onSubmit.bind(this), { capture: true });

      this.render();
    }

    /**
     * The savings note belongs with the price, not with the tier buttons, but
     * the price block is a separate section block rendered further up the page.
     * Relocate the node so it sits directly beneath the price; if the price
     * block is absent it simply stays where Liquid put it.
     */
    moveSavingsUnderPrice() {
      if (!this.savingsEl || !this.themePrice) return;

      const priceBlock = this.themePrice.closest('.product-price');
      if (!priceBlock || !priceBlock.parentNode) return;

      priceBlock.parentNode.insertBefore(this.savingsEl, priceBlock.nextSibling);
    }

    /** Reads the variant id the product form is currently pointing at. */
    currentVariantId() {
      const input = this.section.querySelector('.js-product-form [name="id"]');
      if (input && input.value) return String(input.value);

      const ids = Object.keys(this.data.prices);
      return ids.length ? ids[0] : null;
    }

    onTierChange(evt) {
      if (!evt.target.classList.contains('bundle-tiers__input')) return;
      this.selected = evt.target.value;
      this.render();
    }

    onVariantChange(evt) {
      const variant = evt.detail && evt.detail.variant;
      this.variantId = variant ? String(variant.id) : null;
      this.render();
    }

    /** The tier config for the current selection, or null for the single unit. */
    currentTier() {
      if (this.selected === 'single') return null;
      return this.data.tiers[Number(this.selected)] || null;
    }

    /** Precomputed money strings for the current variant + tier. */
    currentPrices() {
      const forVariant = this.variantId ? this.data.prices[this.variantId] : null;
      if (!forVariant) return null;
      if (this.selected === 'single') return null;
      return forVariant.tiers[Number(this.selected)] || null;
    }

    /**
     * Writes into the theme's price element so pack pricing looks like every
     * other price on the site. Passing an empty `was` clears the strikethrough.
     */
    setThemePrice(current, was) {
      if (!this.themePrice || !this.themeCurrent) return;

      this.themeCurrent.textContent = current;
      if (this.themeWas) this.themeWas.textContent = was || '';
      this.themePrice.classList.toggle('price--on-sale', Boolean(was));
    }

    /** Restores the plain single-unit price for the current variant. */
    restoreThemePrice() {
      const forVariant = this.variantId ? this.data.prices[this.variantId] : null;
      if (!forVariant) return;
      this.setThemePrice(forVariant.single, forVariant.compareAt);
    }

    render() {
      const tier = this.currentTier();
      const prices = this.currentPrices();

      this.setQuantity(tier ? tier.quantity : 1);
      this.setPackSize(tier ? tier.quantity : 0);

      if (!tier || !prices) {
        // Only restore if we previously wrote to the theme's price - on first
        // load the theme's own render is already correct and must be left alone.
        if (this.hasOverridden) {
          this.restoreThemePrice();
          this.hasOverridden = false;
        }
        if (this.savingsEl) this.savingsEl.hidden = true;
        return;
      }

      // Struck-through full price only makes sense when the tier saves money.
      const discounted = tier.discountPercent > 0;
      this.setThemePrice(prices.packed, discounted ? prices.full : '');
      this.hasOverridden = true;

      if (this.savingsEl) {
        this.savingsEl.hidden = !discounted;
        if (discounted) this.savingsEl.innerHTML = prices.savingsHtml;
      }
    }

    /**
     * Locks the theme quantity input to the tier quantity. `readonly` alone does
     * not stop Symmetry's <quantity-wrapper> +/- buttons, hence the pointer-events
     * guard; aria-disabled keeps the state discoverable to assistive tech.
     */
    setQuantity(quantity) {
      const input = this.section.querySelector('.js-product-form [name="quantity"]');
      const wrapper = this.section.querySelector('.quantity-wrapper');
      const locked = quantity > 1;

      if (input) {
        input.value = quantity;
        input.toggleAttribute('readonly', locked);
        input.setAttribute('aria-disabled', locked ? 'true' : 'false');
      }

      if (wrapper) {
        wrapper.classList.toggle('quantity-wrapper--locked', locked);
        wrapper.style.pointerEvents = locked ? 'none' : '';
      }
    }

    /**
     * Records the chosen pack size on the line via `properties[_pack_size]`,
     * which the cart reads to step the line in whole packs. A pack size of 0
     * (the single unit) disables the field so nothing is submitted at all.
     */
    setPackSize(quantity) {
      if (!this.packSizeEl) return;

      const packed = quantity > 1;
      this.packSizeEl.value = packed ? String(quantity) : '';
      this.packSizeEl.disabled = !packed;
    }

    onSubmit(evt) {
      const form = evt.target.closest && evt.target.closest('.js-product-form');
      if (!form) return;

      const tier = this.currentTier();
      if (!tier) return;

      const input = form.querySelector('[name="quantity"]');
      if (input) input.value = tier.quantity;

      // Re-assert in case something else in the form was re-rendered between
      // the tier change and the submit.
      this.setPackSize(tier.quantity);
    }
  }

  customElements.define('bundle-quantity-tiers', BundleQuantityTiers);
}
